import { EventInfo } from "@components/EventInfo";
import { DraggableEventInfo } from "@components/EventInfo/DraggableEventInfo";
import { InstantImage } from "@components/FirebaseCachedImage/InstantImage";
import { HighscoreTable } from "@components/HighscoreTable";
import { CrossIcon } from "@components/Icons/Cross";
import { CycleIcon } from "@components/Icons/Cycle";
import { FullscreenIcon } from "@components/Icons/Fullscreen";
import { GridIcon } from "@components/Icons/Grid";
import { HighscoresIcon } from "@components/Icons/Highscores";
import { ImageIcon } from "@components/Icons/Image";
import { QRIcon } from "@components/Icons/QR";
import { TrophyIcon } from "@components/Icons/Trophy";
import { Loading } from "@components/Loading";
import { useTranslation } from "@helpers/useTranslation";
import { Timestamp } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { useContext, useEffect, useMemo, useReducer, useState } from "react";
import { FirebaseContext } from "src/helpers/firebase";
import { ImageCacheContext } from "src/helpers/imageCache";
import { useBranding } from "src/helpers/useBranding";
import { useEvent, useEventId } from "src/helpers/useEvent";
import { useFullScreen } from "src/helpers/useFullScreen";
import { useInterval } from "src/helpers/useInterval";
import { useSimpleCollection } from "src/helpers/useSimpleCollection";
import { useToast } from "src/helpers/useToast";

type ImageURI = {
  key: string;
  id: string;
  timestamp: Timestamp;
  uri: string;
  name: string;
  index?: number;
  challenge?: string;
};

type ImageURL = ImageURI & {
  url: string;
};

type Order = "NewestFirst" | "LatestSolutionFirst" | "LatestExtraFirst";

const sortByFirebaseTimestamp = (a: ImageURI, b: ImageURI) =>
  (b?.timestamp?.toMillis() || 0) - (a?.timestamp?.toMillis() || 0);

/* Randomize array in-place using Durstenfeld shuffle algorithm */
const shuffle = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
};

export const Slideshow = () => {
  const { t } = useTranslation("admin");
  const eventId = useEventId();
  const [teams, tLoading] = useSimpleCollection<Team>(`events/${eventId}/teams`);
  const [challenges, cLoading] = useSimpleCollection<Challenge>(`events/${eventId}/challenges`);
  const [currentTeams, setCurrentTeams] = useState<Team[]>([]);

  // Betting results component
  const BettingResultsView = ({ compact: _compact = false }: { compact?: boolean }) => {
    if (!firstBettingChallenge) {
      return (
        <div className="text-center text-neutral">
          <p>{t("noBettingChallengesFound")}</p>
        </div>
      );
    }

    const isBettingClosed = firstBettingChallenge.bettingsClosed;
    const bettingResults = firstBettingChallenge.bettingResults || [];
    const paymentTransactions = firstBettingChallenge.paymentTransactions || [];

    if (!isBettingClosed) {
      return (
        <div className="text-center text-neutral">
          <p className="mb-4 text-lg">{t("bettingNotClosedYet")}</p>
          <p className="text-sm opacity-70">{t("bettingNotClosedMessage")}</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Scenarios and Outcomes - Compact Grid */}
        <div>
          <h3 className="mb-2 text-lg font-semibold text-neutral">{t("finalOutcomes")}</h3>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {firstBettingChallenge.scenarios.map((scenario, index) => {
              const correctOutcome = scenario.correctOutcome;
              const optionKey = `option${correctOutcome}` as keyof BettingScenario;
              const correctOptionText = correctOutcome !== undefined ? (scenario[optionKey] as string) : "Not resolved";

              return (
                <div key={scenario.id || index} className="rounded border border-neutral bg-neutral bg-opacity-10 p-2">
                  <p className="mb-1 truncate text-xs font-semibold" title={scenario.scenario}>
                    {scenario.scenario}
                  </p>
                  <p className="truncate text-xs text-neutral" title={correctOptionText}>
                    ✓ {correctOutcome !== undefined ? `${correctOptionText}` : "Not resolved"}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Team Results - Compact Table */}
        <div>
          <h3 className="mb-2 pt-4 text-lg font-semibold text-neutral">{t("teamResults")}</h3>
          <div className="grid max-h-64 grid-cols-1 gap-1 overflow-y-auto">
            {bettingResults.map((result, index) => (
              <div
                key={result.teamId}
                className={`flex items-center justify-between rounded border py-2 px-3 ${
                  result.netResult > 0
                    ? "border-success bg-success bg-opacity-10"
                    : result.netResult < 0
                    ? "border-error bg-error bg-opacity-10"
                    : "border-base-300 bg-base-200"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold" title={result.teamName}>
                    {index + 1}. {result.teamName}
                  </p>
                  <p className="text-xs opacity-70">
                    {result.correctBets}/{result.totalBets} correct
                  </p>
                </div>
                <div className="ml-2 text-right">
                  <p
                    className={`text-sm font-semibold ${
                      result.netResult > 0 ? "text-success" : result.netResult < 0 ? "text-error" : "text-neutral"
                    }`}
                  >
                    {result.netResult > 0 ? "+" : ""}${result.netResult.toFixed(0)}
                  </p>
                  <p className="text-xs opacity-70">
                    ${result.winnings.toFixed(0)}/${result.totalBetAmount}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Summary - Compact List */}
        {paymentTransactions.length > 0 && (
          <div>
            <h3 className="mb-2 pt-4 text-lg font-semibold text-neutral">{t("paymentSummary")}</h3>
            <div className="grid max-h-32 grid-cols-1 gap-1 overflow-y-auto">
              {paymentTransactions.map((transaction, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between rounded border border-base-300 bg-base-200 py-1 px-2"
                >
                  <p
                    className="min-w-0 flex-1 truncate text-xs"
                    title={`${transaction.fromTeamName} owes ${transaction.toTeamName}`}
                  >
                    <span className="font-medium">{transaction.fromTeamName}</span> →{" "}
                    <span className="font-medium">{transaction.toTeamName}</span>
                  </p>
                  <p className="ml-2 text-xs font-semibold text-primary">${transaction.amount.toFixed(0)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const [imageUris, setImageUris] = useState<ImageURI[]>([]);
  const [imageURLs, setImageURLs] = useState<ImageURL[]>([]);
  const [event, loading] = useEvent();
  const { toggleFullscreen } = useFullScreen();
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [qrCodeOpen, setQrCodeOpen] = useState(false);
  const onQrCodeClose = () => setQrCodeOpen(false);
  const [showHighscores, setShowHighscores] = useState(false);
  const hideHighscores = event?.mainConfig?.hideHighscores ?? false;
  const [showBettingResults, setShowBettingResults] = useState(false);

  // Check if betting is enabled and there are betting challenges
  const bettingEnabled = event?.mainConfig?.bettingEnabled ?? false;
  const bettingChallenges = challenges.filter((c) => c.type === "betting");
  const showBettingIcon = bettingEnabled && bettingChallenges.length > 0;
  const firstBettingChallenge = bettingChallenges[0] as BettingChallenge | undefined;

  const cache = useContext(ImageCacheContext);
  const { storage } = useContext(FirebaseContext);

  const pollRetryDownload = async (downloadRef, retries, waitTime = 1500) => {
    try {
      const url = await getDownloadURL(downloadRef);
      return url;
    } catch (e) {
      if (retries <= 1) {
        throw e;
      }
      await new Promise((resolve) => setTimeout(resolve, waitTime));
      return pollRetryDownload(downloadRef, retries - 1);
    }
  };

  const [shuffleEnabled, setShuffleEnabled] = useState(false);
  const toggleShuffle = () => {
    if (shuffleEnabled) toast(t("shuffleDisabled"), "🔀", "error", 4000);
    else toast(t("shuffleEnabled"), "🔀", "success", 4000);

    setShuffleEnabled(!shuffleEnabled);
  };

  useInterval(() => {
    if (shuffleEnabled) {
      const solutionURIs = getSolutionURIs();
      const extrasURIs = getExtrasURIs();
      const allUris = [...solutionURIs, ...extrasURIs];
      const URIsWithIndex = allUris.map((uri, index) => ({ ...uri, index: index + 1 }));
      const newImageURIs = [...URIsWithIndex];
      shuffle(newImageURIs);
      setImageUris(newImageURIs);
    }
  }, 10000);

  const buildImageURLs = async () => {
    const targetNumberOfImages = previewMode === "grid" ? 4 : 1;

    const urls: ImageURL[] = [];
    for (const imageUri of imageUris) {
      const gotEnoughImages = urls.length >= targetNumberOfImages;
      if (gotEnoughImages) break;

      const { uri } = imageUri;
      const lightUri = `${uri}_light`;

      // Check cache first
      const cachedUrl = cache.current.get(lightUri);
      if (cachedUrl) {
        urls.push({ ...imageUri, url: cachedUrl });
        continue;
      }

      // Download fresh
      try {
        const url = await pollRetryDownload(ref(storage, lightUri), 3);
        urls.push({ ...imageUri, url });
      } catch (e) {
        console.log("Error downloading image", e);
      }
    }

    setImageURLs(urls);
  };

  useEffect(() => {
    if (!imageUris.length) return;
    buildImageURLs();
  }, [imageUris]);

  const greetingChallengeIds = useMemo(
    () => new Set(challenges.filter((c) => c.type === "greeting").map((c) => c.id)),
    [challenges],
  );

  const isSlideshowSolution = (solutionId: string) => {
    const baseId = solutionId.endsWith("-video") ? solutionId.slice(0, -"-video".length) : solutionId;
    return !greetingChallengeIds.has(baseId);
  };

  const getSolutionURIs = () =>
    (teams ?? []).flatMap((team) => {
      return Object.keys(team.solutionsMap || {})
        .filter(isSlideshowSolution)
        .map((solution) => {
          const challenge = challenges.find((challenge) => challenge.id === solution);
          return {
            key: `${team.id}${solution}`,
            id: solution,
            timestamp: team.solutionsMap[solution],
            uri: `events/${eventId}/teams/${team.id}/solutions/${solution}`,
            name: `${team.displayName ?? team.name}`,
            challenge: challenge?.title ?? challenge?.title_en ?? challenge?.title_lt ?? "",
          };
        });
    });

  const getExtrasURIs = () => {
    const ignoreExtras = eventId === "beaujolaisvakaras";
    if (ignoreExtras) return [];

    const extrasURIs: ImageURI[] = (teams ?? []).flatMap((team) => {
      return Object.keys(team.extrasMap || {})
        .filter((extra) => !extra.endsWith("-video"))
        .flatMap((extra) => {
          const extraEntry = team.extrasMap[extra];
          // Handle both legacy (Timestamp) and new format (ExtraMapEntry)
          const timestamp =
            extraEntry instanceof Object && "timestamp" in extraEntry ? extraEntry.timestamp : extraEntry;

          // Get challenge name for challenge-linked extras
          const linkedChallengeId =
            extraEntry instanceof Object && "challengeId" in extraEntry ? extraEntry.challengeId : undefined;
          const linkedChallenge = linkedChallengeId ? challenges.find((c) => c.id === linkedChallengeId) : undefined;
          if (linkedChallenge?.type === "greeting") return [];

          return [
            {
              key: `${team.id}${extra}`,
              id: extra,
              timestamp,
              uri: `events/${eventId}/teams/${team.id}/extras/${extra}`,
              name: `${team.displayName ?? team.name}`,
              challenge: linkedChallenge?.title ?? linkedChallenge?.title_en ?? linkedChallenge?.title_lt ?? "",
            },
          ];
        });
    });
    return extrasURIs;
  };

  const recomputeURIs = (order?: Order) => {
    const solutionURIs = getSolutionURIs();
    const extrasURIs = getExtrasURIs();
    let recomputedURIs = [];

    if (order === "NewestFirst") {
      const URIs = [...solutionURIs, ...extrasURIs];
      URIs.sort(sortByFirebaseTimestamp);
      recomputedURIs = URIs;
    }

    if (order === "LatestExtraFirst") {
      const sortedExtraURIs = [...extrasURIs];
      sortedExtraURIs.sort(sortByFirebaseTimestamp);
      if (!sortedExtraURIs.length) return [];

      const [latestExtra, ...restOfExtraURIs] = sortedExtraURIs;
      const restOfURIs = [...solutionURIs, ...restOfExtraURIs];
      shuffle(restOfURIs); // Randomise the rest
      recomputedURIs = [latestExtra, ...restOfURIs];
    }

    if (order === "LatestSolutionFirst") {
      const sortedSolutionURIs = [...solutionURIs];
      sortedSolutionURIs.sort(sortByFirebaseTimestamp);
      if (!sortedSolutionURIs.length) return [];

      const [latestSolution, ...restOfSolutionURIs] = sortedSolutionURIs;
      const restOfURIs = [...restOfSolutionURIs, ...extrasURIs];
      shuffle(restOfURIs); // Randomise the rest
      recomputedURIs = [latestSolution, ...restOfURIs];
    }

    const URIsWithIndex = recomputedURIs.map((uri, index) => ({ ...uri, index: index + 1 }));
    return URIsWithIndex;
  };

  const toast = useToast();

  useEffect(() => {
    if (tLoading || cLoading || !teams.length) return;

    const firstLoad = currentTeams.length === 0;
    if (firstLoad) {
      setCurrentTeams(teams);

      const newImageUris = recomputeURIs("NewestFirst");
      setImageUris(newImageUris);
      return;
    }

    const newTeamCreated = teams.length > currentTeams.length;
    if (newTeamCreated) {
      const newTeam = teams.find((team) => !currentTeams.find((currentTeam) => currentTeam.id === team.id));
      toast(`${newTeam.displayName ?? newTeam.name} ${t("newTeamJoined")}`, "🥳", "success", 4000);
      setCurrentTeams([...currentTeams, newTeam]);

      const newImageUris = recomputeURIs("NewestFirst");
      setImageUris(newImageUris);
      return;
    }

    // Check if a new solution (or new extra) has been submitted
    for (const newTeam of teams) {
      const currentTeam = currentTeams.find((currentTeam) => currentTeam.id === newTeam.id);
      if (!currentTeam) continue;

      const newSolution = Object.keys(newTeam.solutionsMap || {}).find(
        (solution) => !Object.keys(currentTeam.solutionsMap || {}).includes(solution),
      );
      if (newSolution) {
        const challenge = challenges.find((challenge) => challenge.id === newSolution);
        if (!challenge) continue;
        setCurrentTeams(teams);

        if (isSlideshowSolution(newSolution)) {
          const challengeTitle = challenge.title ?? challenge.title_en ?? challenge.title_lt ?? "";
          toast(`${newTeam.displayName ?? newTeam.name} ${t("completed")} ${challengeTitle}`, "🥳", "success", 4000);
          const newImageUris = recomputeURIs("LatestSolutionFirst");
          setImageUris(newImageUris);
          forceUpdate();
        }
        break;
      }

      const newExtras = Object.keys(newTeam.extrasMap || {}).length > Object.keys(currentTeam.extrasMap || {}).length;
      if (newExtras) {
        toast(`${newTeam.displayName ?? newTeam.name} ${t("uploadedNewExtras")}`, "📸", "success", 4000);
        setCurrentTeams(teams);

        const newImageUris = recomputeURIs("LatestExtraFirst");
        setImageUris(newImageUris);
        break;
      }
    }
  }, [teams, challenges, tLoading, cLoading]);

  const [previewMode, setPreviewMode] = useState<"single" | "grid">("grid");
  const togglePreviewMode = () => setPreviewMode(previewMode === "grid" ? "single" : "grid");

  const gridImages = useMemo(() => {
    const images = new Array(4).fill(null).map((_, index) => {
      const image = imageURLs[index];
      if (!image) return null;
      return { ...image, key: `${image.key}${index}` };
    });
    return images;
  }, [imageURLs]);

  const singleImage = useMemo(() => (imageURLs.length ? imageURLs[0] : null), [imageURLs]);

  const { homepage, brandName, brand, slideshowLogoUrl } = useBranding();

  const imagesExist = imageUris.length > 0;

  const [eventLogoUrl, setEventLogoUrl] = useState<string>();

  useEffect(() => {
    if (!eventId) return;

    const fetchEventLogo = async () => {
      try {
        const logoRef = ref(storage, `events/${eventId}/logo.png`);
        const url = await getDownloadURL(logoRef);
        setEventLogoUrl(url);
      } catch (e) {
        // console.log("No event logo found");
      }
    };

    fetchEventLogo();
  }, [eventId, storage]);

  return (
    <div>
      <div className={`absolute right-5 z-50 hidden text-white md:block`}>
        <a className="ml-6 p-2" onClick={toggleFullscreen}>
          <FullscreenIcon className="h-8 w-8" strokeWidth={1.2} />
        </a>
        <a className="ml-6 p-2" onClick={togglePreviewMode}>
          {previewMode === "grid" ? (
            <ImageIcon className="h-8 w-8" strokeWidth={1.2} />
          ) : (
            <GridIcon className="h-8 w-8" strokeWidth={1.2} />
          )}
        </a>
        <a className="ml-6 p-2" onClick={() => setQrCodeOpen(!qrCodeOpen)}>
          <QRIcon className="h-8 w-8" strokeWidth={1.2} />
        </a>
        <a className="ml-6 p-2" onClick={toggleShuffle}>
          {shuffleEnabled ? (
            <CrossIcon className="h-8 w-8" strokeWidth={1.2} />
          ) : (
            <CycleIcon className="h-8 w-8" strokeWidth={1.2} />
          )}
        </a>
        {!hideHighscores && (
          <a className="ml-6 p-2" onClick={() => setShowHighscores(!showHighscores)}>
            <HighscoresIcon className="h-8 w-8" strokeWidth={1.2} />
          </a>
        )}
        {showBettingIcon && (
          <a className="ml-6 p-2" onClick={() => setShowBettingResults(!showBettingResults)}>
            <TrophyIcon className="h-8 w-8 text-white" strokeWidth={1.2} />
          </a>
        )}
      </div>
      {!loading && imagesExist && (
        <DraggableEventInfo
          open={qrCodeOpen}
          onClose={onQrCodeClose}
          eventId={eventId}
          pin={event.pin}
          title={t("scanToJoin")}
        />
      )}
      <div>
        <Loading loading={tLoading || cLoading || loading}>
          <div className="absolute top-0 z-40 flex w-full items-end justify-between">
            <a href={homepage} className="ml-6">
              <img src={slideshowLogoUrl} alt={brandName} className="mx-auto ml-4 mt-2 w-64" />
            </a>

            <div className={`mb-6 mt-8 ml-4 flex w-full flex-row justify-start`}>
              <div className={`relative mt-6 ml-4 flex h-5 w-5`}>
                <span
                  className={`absolute mt-2 h-5 w-5 animate-ping rounded-full bg-red-600 opacity-75  ${
                    brand === "tuokis" ? "-mt-4 sm:-mt-12" : ""
                  }`}
                ></span>
                <span
                  className={`relative mt-2 h-5 w-5 rounded-full border border-white border-opacity-100 bg-red-600  ${
                    brand === "tuokis" ? "-mt-4 sm:-mt-12" : ""
                  }`}
                ></span>
              </div>
            </div>
            <div className="md:w-28"></div>
          </div>
          <div className="h-auto w-full">
            <div className="scroll-auto">
              {!loading && !imagesExist && (
                <div className="h-screen w-full bg-neutral text-white">
                  <div className="flex w-full flex-col items-center justify-center pt-16">
                    <h1 className="mt-16 mb-4 max-w-lg text-center text-5xl font-semibold">{event.name}</h1>
                    <EventInfo size="lg" eventId={eventId} pin={event.pin} border white />
                    <h2 className="text-1xl mt-8 mb-4 max-w-lg text-center font-normal">{t("scanToJoin")}</h2>
                  </div>
                </div>
              )}

              {imagesExist && (
                <>
                  <div className="absolute z-10 h-full w-full bg-black object-cover opacity-90 blur" />
                  {previewMode === "grid" && (
                    <div className="z-20 grid h-screen grid-cols-2 grid-rows-2 border-4 border-black ">
                      {gridImages.map((image, index) => {
                        const lastImage = index === 3;
                        if (lastImage && showHighscores && !hideHighscores)
                          return (
                            <div className="z-10 overflow-auto border-4 border-black bg-base-100">
                              <div className="p-6">
                                <h2 className="mb-4 text-center text-3xl font-semibold text-neutral">
                                  {t("highscores")}
                                </h2>
                                <HighscoreTable compact key="highscores" />
                              </div>
                            </div>
                          );

                        if (lastImage && showBettingResults && showBettingIcon)
                          return (
                            <div className="z-10 overflow-auto border-4 border-black bg-base-100">
                              <div className="p-6">
                                <h2 className="mb-4 text-center text-3xl font-semibold text-neutral">
                                  {t("bettingResults")}
                                </h2>
                                <BettingResultsView compact key="betting-results" />
                              </div>
                            </div>
                          );

                        if (!image) return <div className="z-10 overflow-auto border-4 border-black bg-neutral" />;
                        const { url, name, key, challenge } = image;
                        return (
                          <div className="relative z-10 overflow-hidden border-4 border-black" key={key}>
                            <InstantImage
                              className="z-30 h-full w-full animate-slow-fade-in object-cover opacity-70 blur"
                              url={url}
                            />
                            <div className="z-20 flex w-full  justify-center">
                              <InstantImage className="absolute	top-0 h-full animate-fade-in object-contain" url={url} />
                            </div>
                            <h2 className="absolute bottom-0 mb-4 w-full text-center text-white">
                              <div className="text-xl font-semibold">{name}</div>
                              {challenge && <div className="text-sm">{challenge}</div>}
                            </h2>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {previewMode === "single" && (
                    <div className="absolute top-0 h-full w-full border-4 border-black">
                      <div className="relative z-30 h-full w-full overflow-hidden border-4 border-black">
                        {singleImage && (
                          <>
                            <InstantImage
                              key={singleImage.key}
                              className="z-30 h-full w-full animate-slow-fade-in object-cover opacity-70 blur"
                              url={singleImage.url}
                            />
                            <div className="flex w-full animate-slow-fade-in justify-center">
                              <InstantImage
                                key={singleImage.key}
                                className="absolute top-0 h-full animate-fade-in object-contain"
                                url={singleImage.url}
                              />
                            </div>
                          </>
                        )}
                        {showHighscores && !hideHighscores ? (
                          <div className="absolute top-0 flex h-full w-full justify-center overflow-auto bg-base-300 opacity-95">
                            <div className="mt-24 h-full w-full max-w-2xl p-12">
                              <h2 className="mb-4 text-center text-3xl font-semibold text-neutral">
                                {t("highscores")}
                              </h2>
                              <HighscoreTable limit={25} />
                            </div>
                          </div>
                        ) : showBettingResults && showBettingIcon ? (
                          <div className="absolute top-0 flex h-full w-full justify-center overflow-auto bg-base-200 opacity-95">
                            <div className="mt-24 h-full w-full max-w-4xl p-12">
                              <h2 className="mb-4 text-center text-3xl font-semibold text-neutral">
                                {t("bettingResults")}
                              </h2>
                              <BettingResultsView />
                            </div>
                          </div>
                        ) : (
                          <h2 className="absolute bottom-0 mb-12 w-full text-center text-white">
                            <div className="text-4xl font-semibold">{singleImage?.name ?? ""}</div>
                            {singleImage?.challenge && <div className="text-xl">{singleImage?.challenge}</div>}
                          </h2>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </Loading>
      </div>
      {eventLogoUrl && (
        <div className="fixed bottom-8 left-8 z-50">
          <img src={eventLogoUrl} alt="Event logo" className="max-w-64 max-h-32 object-contain" />
        </div>
      )}
    </div>
  );
};
